import { onMounted, watch, nextTick } from "vue";
import { ApolloQueryResult } from "@apollo/client/core";
import { getWordList } from "@/common/graphql/expression";
import { getChainInfo, getChainList } from "@/common/graphql/chain";
import { generateApolloClient } from "@/common/apolloClientUtil";
import {
  localStorageGetAuthType,
  localStorageGetChainCode,
  localStorageGetCustomerCode,
  localStorageGetUserId,
} from "@/common/localStorageUtil";
import { DeliverableDisplay } from "@/common/constant";
import {
  GetShopDocument,
  GetShopQuery,
} from "@/generated/graphql";
import shopStore from "@/store/shopStore";
import type { ShopRegisterInitDeps } from "../types";
import {
  SHOP_REGISTER_FILE_NAME,
  REGISTRATION_DELIVERY_REQUEST_FILE_NAME,
} from "../constants";

/** 店舗登録画面の初期化を扱う composable。 */
export const useShopRegisterInit = (deps: ShopRegisterInitDeps) => {
  const {
    shopCreateInput,
    wordList,
    deliveryRequestwordList,
    chainListItems,
    chainSelectValues,
    chainViewFlag,
    useReceiptBarcodeSearch,
    updateMode,
    dssFlg,
    clickedBlockAddress,
    shippingLabelPrintSelectValues,
    shippingLabelPrintItems,
    deliveryOrderPrintSelectValues,
    deliveryOrderPrintItems,
    tmpMemberRegistrationSelectValues,
    tmpMemberRegistrationItems,
    dssSelectValues,
    dssListItems,
    formRef,
    isWordListReady,
    onChangeDeliverable,
    getListDssConnectionSetting,
    onBlurDssShopCd,
    updateModeSetHoliday,
    updateModeSetDelivarable,
    syncFormValues,
    allClear,
    listEditFlag,
    displayConfirmDialog,
  } = deps;

  onMounted(async () => {
    //graphql接続
    const apolloClient = await generateApolloClient();

    //文言データを取得する
    Object.assign(
      wordList.value,
      await getWordList(
        localStorageGetChainCode(),
        SHOP_REGISTER_FILE_NAME,
        localStorageGetCustomerCode(),
      ),
    );

    //配送依頼登録の文言データを取得する
    Object.assign(
      deliveryRequestwordList.value,
      await getWordList(
        localStorageGetChainCode(),
        REGISTRATION_DELIVERY_REQUEST_FILE_NAME,
        localStorageGetCustomerCode(),
      ),
    );

    //配達可能残数の表示
    await onChangeDeliverable(DeliverableDisplay.symbol);

    //運用管理者以外の場合、チェーンコードはログインユーザのチェーンコードにする
    chainListItems.value =
      (await getChainList(localStorageGetCustomerCode())) ?? [];
    if (chainViewFlag.value) {
      //チェーン情報を取得する
      if (chainSelectValues.value) chainSelectValues.value.chain_code = "";
      shopCreateInput.chain_code = "";
    } else {
      //チェーン情報を設定する
      if (chainSelectValues.value)
        chainSelectValues.value.chain_code = localStorageGetChainCode();
      shopCreateInput.chain_code = localStorageGetChainCode();
    }

    // レシートバーコード検索を使用するかどうかのフラグ
    useReceiptBarcodeSearch.value = false;
    // 本部権限の場合、チェーン情報を取得する
    if (localStorageGetAuthType() === "1") {
      //チェーン情報を取得
      const result = await getChainInfo(
        localStorageGetCustomerCode(),
        localStorageGetChainCode(),
      );

      // レシートバーコード検索を使用するかどうかのフラグが1の場合、trueをセットする
      if (
        result !== undefined &&
        result !== null &&
        result.use_receipt_barcode_search_flg === 1
      ) {
        useReceiptBarcodeSearch.value = true;
      }
    }

    updateMode.value = false;
    // storeからshopInfoを取得する
    if (shopStore.hasValue) {
      try {
        const shopInfo: ApolloQueryResult<GetShopQuery> =
          await apolloClient.query<GetShopQuery>({
            query: GetShopDocument,
            variables: {
              chain_code: shopStore.chain_code,
              customer_code: localStorageGetCustomerCode(),
              shop_code: shopStore.shop_code,
            },
          });
        if (
          shopInfo.data.getShop !== null &&
          shopInfo.data.getShop !== undefined
        ) {
          Object.assign(shopCreateInput, {
            address1: shopInfo.data.getShop.address1,
            address2: shopInfo.data.getShop.address2,
            address3: shopInfo.data.getShop.address3,
            chain_code: shopInfo.data.getShop.chain_code,
            customer_code: shopInfo.data.getShop.customer_code,
            dss_connection_id: shopInfo.data.getShop.dss_connection_id,
            dss_shop_code: shopInfo.data.getShop.dss_shop_code,
            point_server_store_code: shopInfo.data.getShop.point_server_store_code,
            point_server_ip_address: shopInfo.data.getShop.point_server_ip_address,
            point_server_db_name: shopInfo.data.getShop.point_server_db_name,
            fax: shopInfo.data.getShop.fax,
            delivery_request_notification_emails:
              shopInfo.data.getShop.delivery_request_notification_emails,
            name: shopInfo.data.getShop.name,
            name_kana: shopInfo.data.getShop.name_kana,
            phone: shopInfo.data.getShop.phone,
            shop_code: shopInfo.data.getShop.shop_code,
            tmp_member_registration_flg:
              shopInfo.data.getShop.tmp_member_registration_flg,
            zip_code: shopInfo.data.getShop.zip_code,
            user_id: localStorageGetUserId(),
            holiday_fri: shopInfo.data.getShop.holiday_fri,
            holiday_mon: shopInfo.data.getShop.holiday_mon,
            holiday_sat: shopInfo.data.getShop.holiday_sat,
            holiday_sun: shopInfo.data.getShop.holiday_sun,
            holiday_thu: shopInfo.data.getShop.holiday_thu,
            holiday_tue: shopInfo.data.getShop.holiday_tue,
            holiday_wed: shopInfo.data.getShop.holiday_wed,
            alert_lower_threshold: shopInfo.data.getShop.alert_lower_threshold,
            alert_upper_threshold: shopInfo.data.getShop.alert_upper_threshold,
            shipping_label_print_flg:
              shopInfo.data.getShop.shipping_label_print_flg,
            delivery_order_print_flg:
              shopInfo.data.getShop.delivery_order_print_flg,
            double_deli_oricon_count:
              shopInfo.data.getShop.double_deli_oricon_count,
            charter_deli_oricon_count:
              shopInfo.data.getShop.charter_deli_oricon_count,
            lot_code: shopInfo.data.getShop.lot_code,
          });
          const shopChainCode = shopInfo.data.getShop.chain_code;
          const selectedChain = chainListItems.value?.find(
            (chain) => chain?.chain_code === shopChainCode,
          );
          chainSelectValues.value = {
            chain_code: shopChainCode,
            name: selectedChain?.name ?? "",
          };

          //荷札印刷
          if (shopInfo.data.getShop.shipping_label_print_flg !== undefined) {
            shopCreateInput.shipping_label_print_flg =
              shopInfo.data.getShop.shipping_label_print_flg;
            const found = shippingLabelPrintItems.find(
              (element: { index: number }) =>
                element.index === shopInfo.data.getShop!.shipping_label_print_flg,
            );
            if (found) shippingLabelPrintSelectValues.value = found;
          }

          //配達伝票印刷
          if (shopInfo.data.getShop.delivery_order_print_flg !== undefined) {
            shopCreateInput.delivery_order_print_flg =
              shopInfo.data.getShop.delivery_order_print_flg;
            const found = deliveryOrderPrintItems.find(
              (element: { index: number }) =>
                element.index === shopInfo.data.getShop!.delivery_order_print_flg,
            );
            if (found) deliveryOrderPrintSelectValues.value = found;
          }

          //仮会員登録機能
          if (shopInfo.data.getShop.tmp_member_registration_flg !== undefined) {
            shopCreateInput.tmp_member_registration_flg =
              shopInfo.data.getShop.tmp_member_registration_flg;
            const found = tmpMemberRegistrationItems.find(
              (element: { index: number }) =>
                element.index ===
                shopInfo.data.getShop!.tmp_member_registration_flg,
            );
            if (found) tmpMemberRegistrationSelectValues.value = found;
          }

          //DSS接続先取得
          await getListDssConnectionSetting();

          const _dssConnectionId = shopInfo.data.getShop.dss_connection_id!;

          if (_dssConnectionId > 0) {
            const selectedDss = dssListItems.value?.find(
              (dss) => dss?.connection_id === _dssConnectionId,
            );
            if (selectedDss) {
              Object.assign(dssSelectValues.value!, {
                connection_id: _dssConnectionId,
                connection_name: selectedDss.connection_name!,
              });
            }
          } else {
            Object.assign(dssSelectValues.value!, {
              connection_id: 0,
              connection_name: "連携しない",
            });
            dssFlg.value = false;
          }

          if (_dssConnectionId > 0) {
            await onBlurDssShopCd();
          }

          //休日設定をする
          await updateModeSetHoliday();
          //配達可能残数を設定する
          await updateModeSetDelivarable();
          //アドレスの値の初期化
          shopCreateInput.address1 = shopInfo.data.getShop.address1;
          //更新モード
          updateMode.value = true;
          if ((shopCreateInput.dss_connection_id ?? 0) > 0) {
            dssFlg.value = true;
          }
          //次へ画面に遷移するボタンを有効にする
          clickedBlockAddress.value = true;
          await nextTick();
          syncFormValues();
          //storeにあるショップコードをクリアする
          shopStore.setupValue({
            chain_code: "",
            shop_code: "",
            shipping_label_print_flg: 0,
            delivery_order_print_flg: 0,
          });
        } else {
          //荷札印刷
          shopCreateInput.shipping_label_print_flg = 0;
          const shipFound = shippingLabelPrintItems.find(
            (element: { index: number }) => element.index === 0,
          )!;
          shippingLabelPrintSelectValues.value = shipFound;
          //配達伝票印刷
          shopCreateInput.delivery_order_print_flg = 0;
          const deliFound = deliveryOrderPrintItems.find(
            (element: { index: number }) => element.index === 0,
          )!;
          deliveryOrderPrintSelectValues.value = deliFound;
          //仮会員登録機能フラグ
          shopCreateInput.tmp_member_registration_flg = 0;
          const tmpFound = tmpMemberRegistrationItems.find(
            (element: { index: number }) => element.index === 0,
          );
          if (tmpFound) tmpMemberRegistrationSelectValues.value = tmpFound;
        }
      } catch (err: unknown) {
        if (err instanceof Error) {
          err.message = "店舗情報の取得が失敗しました。";
          throw err;
        } else {
          throw new Error("店舗情報の取得が失敗しました。");
        }
      }
    }
  });

  // 店舗一覧編集関係
  watch(
    listEditFlag,
    async () => {
      displayConfirmDialog.value = false;
      shopCreateInput.shop_code = "";
      allClear();
    },
    { deep: true },
  );
};
